/**
 * CategoriesSettings - Service-Kategorien Verwaltung
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tag, Plus, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CategoriesSettings = () => {
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/service-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Bitte geben Sie einen Kategorienamen ein');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/service-categories`, 
        { name: newCategoryName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Kategorie hinzugefügt');
      setNewCategoryName('');
      loadCategories();
    } catch (error) {
      toast.error('Fehler beim Hinzufügen der Kategorie');
    }
  };

  const handleUpdateCategory = async (categoryId) => {
    if (!editCategoryName.trim()) {
      toast.error('Bitte geben Sie einen Kategorienamen ein');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/service-categories/${categoryId}`,
        { name: editCategoryName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Kategorie aktualisiert');
      setEditingCategory(null);
      setEditCategoryName('');
      loadCategories();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren der Kategorie');
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Möchten Sie die Kategorie "${categoryName}" wirklich löschen?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/service-categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kategorie gelöscht');
      loadCategories();
    } catch (error) {
      toast.error('Fehler beim Löschen der Kategorie');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Tag size={20} />
          Service-Kategorien
        </CardTitle>
        <CardDescription>
          Verwalten Sie Kategorien für Ihre Services. Diese werden für Umsatzanalysen und Berichte verwendet.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Neue Kategorie hinzufügen */}
        <div className="flex gap-2">
          <Input
            placeholder="Neue Kategorie..."
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
          />
          <Button onClick={handleAddCategory}>
            <Plus size={16} className="mr-2" />
            Hinzufügen
          </Button>
        </div>

        {/* Kategorien Liste */}
        <div className="space-y-2">
          {categories.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              Keine Kategorien vorhanden. Fügen Sie Ihre erste Kategorie hinzu!
            </p>
          ) : (
            categories.map((category) => (
              <div
                key={category.id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
              >
                {editingCategory === category.id ? (
                  <div className="flex-1 flex gap-2">
                    <Input
                      value={editCategoryName}
                      onChange={(e) => setEditCategoryName(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') handleUpdateCategory(category.id);
                        if (e.key === 'Escape') setEditingCategory(null);
                      }}
                      autoFocus
                    />
                    <Button
                      size="sm"
                      onClick={() => handleUpdateCategory(category.id)}
                    >
                      Speichern
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingCategory(null);
                        setEditCategoryName('');
                      }}
                    >
                      Abbrechen
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <Tag size={16} className="text-gray-400" />
                      <span className="font-medium">{category.name}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingCategory(category.id);
                          setEditCategoryName(category.name);
                        }}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDeleteCategory(category.id, category.name)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CategoriesSettings;
